import type {CreateExtArgs} from '@wix/document-manager-core'
import type {CompRef} from '@wix/document-services-types'
import {EVENTS} from '../components'
import {getComponentType} from '../../../utils/dalUtils'
import {SLIDE_COMPONENT_TYPES, verifySlideShowStructureOnDelete} from './removeBeforeHooks/slideShowRemoveHook'
import {cleanupCompsInSlotsInRemoval} from './removeBeforeHooks/slotsRemoveHook'
import {cleanupMobileOnlyChildren} from './removeBeforeHooks/mobileComponentRemoveHook'
import {handleAppWidgetDeletion} from './removeBeforeHooks/appWidgetRemoveHook'
import {handleColumnDeletion} from './removeBeforeHooks/columnsRemoveHook'
import {beforePageRemoveHook} from './removeBeforeHooks/pageRemoveHook'
import {
    saveOverrides,
    removeAllOverrides,
    removeInternalRefOverrides
} from './removeBeforeHooks/referredComponentsRemoveHooks'
import {repeaterBeforeRemove} from './removeBeforeHooks/repeaterRemoveHook'
import {cleanupPageSpecificData} from './removeBeforeHooks/pageCleanupRemoveHook'
import {COMP_TYPES} from '../../../constants/constants'

const COLUMN_TYPE = 'wysiwyg.viewer.components.Column'
const APP_PAGE_TYPE = 'wixapps.integration.components.AppPage'
const PAGE_CLEANUP_TYPES = [COMP_TYPES.PAGE_TYPE, APP_PAGE_TYPE]

export const registerRemovalHooks = (createExtArgs: CreateExtArgs) => {
    const {dal, eventEmitter} = createExtArgs

    const resolveCompType = (compPtr: CompRef, compType?: string): string => compType ?? getComponentType(dal, compPtr)

    eventEmitter.addListener(
        EVENTS.COMPONENTS.BEFORE_REMOVE,
        (compPtr: CompRef, compType?: string, delParent?: boolean) => {
            const resolvedType = resolveCompType(compPtr, compType)
            if (SLIDE_COMPONENT_TYPES.includes(resolvedType)) {
                verifySlideShowStructureOnDelete(createExtArgs, compPtr, resolvedType, delParent ?? false)
            }
        }
    )

    eventEmitter.addListener(
        EVENTS.COMPONENTS.BEFORE_REMOVE,
        (compPtr: CompRef, _compType?: string, delParent?: boolean) => {
            cleanupCompsInSlotsInRemoval(createExtArgs, compPtr, delParent ?? false)
        }
    )

    eventEmitter.addListener(EVENTS.COMPONENTS.BEFORE_REMOVE, (compPtr: CompRef, compType?: string) => {
        const resolvedType = resolveCompType(compPtr, compType)
        if (resolvedType === COMP_TYPES.APP_WIDGET_TYPE) {
            handleAppWidgetDeletion(createExtArgs, compPtr)
        }
    })

    eventEmitter.addListener(EVENTS.COMPONENTS.BEFORE_REMOVE, (compPtr: CompRef, compType?: string) => {
        const resolvedType = resolveCompType(compPtr, compType)
        if (resolvedType === COMP_TYPES.REF_TYPE) {
            saveOverrides(createExtArgs, compPtr)
        }
    })

    eventEmitter.addListener(EVENTS.COMPONENTS.AFTER_REMOVE, (compPtr: CompRef, compType?: string) => {
        const resolvedType = resolveCompType(compPtr, compType)
        if (resolvedType === COMP_TYPES.REF_TYPE) {
            removeAllOverrides(createExtArgs, compPtr)
        }
    })

    eventEmitter.addListener(EVENTS.COMPONENTS.AFTER_REMOVE, (compPtr: CompRef) => {
        removeInternalRefOverrides(createExtArgs, compPtr)
    })

    eventEmitter.addListener(
        EVENTS.COMPONENTS.BEFORE_REMOVE,
        (compPtr: CompRef, compType?: string, delParent?: boolean) => {
            const resolvedType = resolveCompType(compPtr, compType)
            if (resolvedType === COLUMN_TYPE) {
                handleColumnDeletion(createExtArgs, compPtr, delParent ?? false)
            }
        }
    )

    eventEmitter.addListener(EVENTS.COMPONENTS.BEFORE_REMOVE, (compPtr: CompRef, compType?: string) => {
        const resolvedType = resolveCompType(compPtr, compType)
        if (resolvedType === COMP_TYPES.PAGE_TYPE) {
            beforePageRemoveHook(createExtArgs, compPtr)
        }
    })

    eventEmitter.addListener(EVENTS.COMPONENTS.BEFORE_REMOVE, (compPtr: CompRef) => {
        cleanupMobileOnlyChildren(createExtArgs, compPtr)
    })

    eventEmitter.addListener(EVENTS.COMPONENTS.BEFORE_REMOVE, (compPtr: CompRef) => {
        repeaterBeforeRemove(createExtArgs, compPtr)
    })

    eventEmitter.addListener(EVENTS.COMPONENTS.BEFORE_REMOVE, (compPtr: CompRef, compType?: string) => {
        const resolvedType = resolveCompType(compPtr, compType)
        if (PAGE_CLEANUP_TYPES.includes(resolvedType)) {
            cleanupPageSpecificData(createExtArgs, compPtr)
        }
    })
}
